// Import base types from SDK
import type {
	ContentSourceFormat,
	CustomForm,
	CustomFormFieldOption,
	CustomFormMetaData,
	IAddress,
	IAttachmentUploadSource,
	IBulkOperationResult,
	IContactFieldOptions,
	ICustomFieldMap,
	IDonationPayment,
	IDonorInfo,
	IFileAttachment,
	IFormField,
	IOperationResult,
	IOperationResultNoValue,
	IRawRichTextContentState,
	IServiceFeeSettings,
	ISurveyReviewRequestSettings,
	ISurveyStats,
	SurveyForm,
	PaymentStatus,
	IBaseApiModel as SDKBaseApiModel,
	IBaseApiTypeModel as SDKBaseApiTypeModel,
} from '../../extViewmodels/sdk/models';
import { DonationFrequency } from './enums/DonationFrequency';
import { SubscriptionStatus } from './enums/SubscriptionStatus';

export type SurveyTypes =
	| 'Survey'
	| 'SatisfactionSurvey'
	| 'EventRegistrationSurvey'
	| 'CustomSurvey'
	| 'DonationSurvey';
export type ApiModelType = SurveyTypes;

// Constrained version of IBaseApiTypeModel to preserve SurveyTypes constraint
export interface IBaseApiTypeModel extends Omit<SDKBaseApiTypeModel, '_type'> {
	_type?: ApiModelType;
}

// Constrained version of IBaseApiModel to preserve SurveyTypes constraint
export interface IBaseApiModel extends Omit<SDKBaseApiModel, '_type'>, IBaseApiTypeModel {
	// id and lastModifiedDate are already included from SDKBaseApiModel via Omit
}

// Re-export for backward compatibility
export type {
	ContentSourceFormat,
	CustomForm,
	CustomFormFieldOption,
	CustomFormMetaData,
	IAddress,
	IBulkOperationResult,
	IContactFieldOptions,
	ICustomFieldMap,
	IDonationPayment,
	IDonorInfo,
	IFileAttachment,
	IFormField,
	IOperationResult,
	IOperationResultNoValue,
	IRawRichTextContentState,
	IServiceFeeSettings,
	ISurveyReviewRequestSettings,
	ISurveyStats,
	PaymentStatus,
};

// Re-export enums
export { DonationFrequency };
export { SubscriptionStatus };

export const asApiError = (e: any) => {
	let error: IOperationResultNoValue = { success: false };
	if (e) {
		if (Object.prototype.hasOwnProperty.call(e, 'systemMessage')) {
			if (Object.prototype.hasOwnProperty.call(e, 'systemCode')) {
				error = e as IOperationResultNoValue;
				error.success = false;
			} else {
				error.systemMessage = e.systemMessage;
			}
		} else if (e instanceof Error) {
			error.systemMessage = e.message;
		} else if (typeof e === 'string') {
			error.systemMessage = e;
		} else if (Object.prototype.hasOwnProperty.call(e, 'toString') && typeof e.toString === 'function') {
			error.systemMessage = e.toString();
		} else {
			error.systemMessage = 'Unexpected error.';
		}

		if (!error.systemCode && (error.systemMessage || '').toLocaleLowerCase() === 'failed to fetch') {
			error.systemCode = -1;
			error.systemMessage = 'Error connecting to Levitate.';
		}
	} else {
		error.systemMessage = 'Unexpected error.';
	}

	return error;
};

export enum HTTPMethod {
	DELETE = 'DELETE',
	GET = 'GET',
	PATCH = 'PATCH',
	POST = 'POST',
	PUT = 'PUT',
}

export interface IApiClientConfig {
	accessToken?: string;
	baseUrl: string;
}

export class ApiClient {
	protected mConfig: IApiClientConfig;

	constructor(config: IApiClientConfig) {
		this.mConfig = config;
	}

	public get baseUrl() {
		return this.mConfig.baseUrl;
	}

	public get = <T>(path: string) => {
		return this.executeRequest<T>(this.buildFetchRequest(path, HTTPMethod.GET));
	};

	public put = <T>(path: string, body?: T) => {
		return this.executeRequest<T>(this.buildFetchRequest(path, HTTPMethod.PUT, body));
	};

	public patch = <T>(path: string, body?: any) => {
		return this.executeRequest<T>(this.buildFetchRequest(path, HTTPMethod.PATCH, body));
	};

	public delete = <T>(path: string) => {
		return this.executeRequest<T>(this.buildFetchRequest(path, HTTPMethod.DELETE));
	};

	public post = <T>(path: string, body?: any) => {
		return this.executeRequest<T>(this.buildFetchRequest(path, HTTPMethod.POST, body));
	};

	public postBulk = <T>(path: string, body?: any) => {
		return this.executeFilePostRequest<T>(this.buildFetchRequest(path, HTTPMethod.POST, body));
	};

	protected buildFetchRequest = (path: string, method: HTTPMethod, obj: any = null) => {
		// Check for a special method called FILEPOST, which we will transform to POST but will not convert body to JSON
		const isFormDataBody = obj instanceof FormData;

		// Define headers
		const headers: HeadersInit = {};
		if (isFormDataBody) {
			// DO NOT INCLUDE A HEADER FOR FILE POSTS
		} else {
			headers['Content-Type'] = 'application/json; charset=utf-8';
		}
		headers['Accept'] = 'application/json';
		headers['Pragma'] = 'no-cache';
		headers['Cache-Control'] = 'no-cache';

		if (this.mConfig.accessToken) {
			headers['Authorization'] = 'bearer ' + this.mConfig.accessToken;
		}

		const url = `${this.mConfig.baseUrl}/${path}`;
		const config: RequestInit = {
			headers,
			method,
			mode: 'cors',
			redirect: 'follow',
		};

		if (method.toLowerCase() !== 'get' && !!obj) {
			config.body = isFormDataBody ? obj : JSON.stringify(obj);
		}

		return { url, ...config };
	};

	protected executeFilePostRequest = async <T>(request: { url: string } & RequestInit): Promise<T[]> => {
		const response = await fetch(request.url, request);
		if (response.status >= 400) {
			throw asApiError(await response.json());
		}
		const apiResponse: IBulkOperationResult<T> = await response.json();
		return apiResponse.succeeded;
	};

	protected executeRequest = async <T>(request: { url: string } & RequestInit): Promise<T> => {
		const response = await fetch(request.url, request);
		if (response.status >= 400) {
			throw asApiError(await response.json());
		}
		const apiResponse: IOperationResult<T> = await response.json();

		return apiResponse.value;
	};
}

export interface ISurvey extends IBaseApiModel {
	_type: SurveyTypes;
	archivedDate?: string; // Date
	companyDisplayName?: string;
	expirationDate?: string; // Date
	intro?: string;
	name?: string;
	responsesAllowed?: boolean;
	startDate?: string; // Date
	surveyForm?: SurveyForm;
}

export interface ISatisfactionSurvey extends ISurvey {
	_type: 'SatisfactionSurvey';
	reviewRequestSettings?: ISurveyReviewRequestSettings;
	customForm?: CustomForm<string>;
	customFormIsDisabled?: boolean;
	customFormMetaData?: CustomFormMetaData[];
	shouldHideFeedback?: boolean;
}

// IFileAttachment is now imported from SDK

export interface IEventInformation {
	location: string;
	startTime: string;
	endTime?: string;
	details: string;
	detailsContent?: IRawRichTextContentState;
	/** Server-rendered plain text of the event details, for calendar (ICS) descriptions and other non-HTML consumers. */
	detailsPlainText?: string;
	image?: IFileAttachment;
	timeZone?: string;
}

/**
 * Base interface for purchasable items in the system
 */
export interface IPurchasableItem extends IBaseApiModel {
	name: string;
	description?: string;
	unitPrice: number; // Price in cents
}

/**
 * Event ticket definition for paid events
 * Extends IPurchasableItem with event-specific properties
 */
export interface IEventTicket extends IPurchasableItem {
	/**
	 * Maximum number of this ticket type that can be sold
	 * null/undefined means unlimited
	 */
	maxAllowed?: number;
	/**
	 * Number of tickets remaining/available for purchase
	 * Calculated as maxAllowed - sold tickets
	 */
	remaining?: number;
	/**
	 * Number of attendees per ticket purchase
	 * e.g., 2 for "Couple's Ticket"
	 */
	attendeeCountPerItem: number;
	/**
	 * Survey ID this ticket belongs to
	 */
	surveyId?: string;
	/**
	 * Whether this is the default ticket shown to users
	 */
	isDefault?: boolean;
	/**
	 * Whether to hide this ticket tier on the event registration page
	 */
	hideTicket?: boolean;
	/**
	 * Whether to show this ticket as sold out
	 */
	showAsSoldOut?: boolean;
}

/**
 * Line item for checkout orders
 * Represents a single item type and quantity in an order
 */
export interface ILineItem {
	/**
	 * Quantity of this item
	 */
	quantity: number;
	/**
	 * The Levitate Internal Product/Ticket ID
	 */
	itemId: string;
	/**
	 * Optional description for the item
	 */
	description?: string;
	/**
	 * Optional name for the item
	 */
	name?: string;
}

export interface IAttendeeOptions {
	enabled?: boolean;
	requireNameAndEmail?: boolean;
	requirePhoneNumber?: boolean;
	guestLimit?: number;
	requireGuestNames?: boolean;
	/**
	 * @deprecated This will become obsolete. Use eventTickets with maxAllowed * attendeeCountPerItem for capacity
	 */
	maximumCapacity?: number;
	registrationDeadline: Date | string;
	/**
	 * @deprecated Legacy cost field. For new paid events, use eventTickets instead
	 */
	costPerAttendee?: number;
	/**
	 * Array of ticket types available for this event
	 * Required for paid events (either this or costPerAttendee must be set)
	 */
	eventTickets?: IEventTicket[];
}

export interface IEventSurveyStatsSubtotals {
	attending?: number;
	notAttending?: number;
	noResponse?: number;
}

export interface IEventResponseStats extends IEventSurveyStatsSubtotals {
	category: string;
}

export interface IEventStats {
	responseStats?: IEventResponseStats[];
}

export interface IEventRegistrationSurvey extends ISurvey {
	_type: 'EventRegistrationSurvey';
	attendeeOptions?: IAttendeeOptions;
	eventInformation?: IEventInformation;
	name?: string;
	stats?: IEventStats;
	customForm?: CustomForm<string>;
	customFormIsDisabled?: boolean;
	isPaidEvent?: boolean;
	payments?: {
		enabled?: boolean;
		cost?: number;
		status?: 'NoPayment' | 'PaymentSucceeded';
	};
}

export interface ISurveyResponse<CustomProperties = Record<string, string>> extends IBaseApiModel {
	contact?: IBaseApiModel;
	surveyId?: string;
	customProperties?: CustomProperties;
	token?: string;
}

export interface ICalculateDonationFeeResponse {
	donationAmount: number;
	feeBreakdown: string;
	netDonationAmount: number;
	paymentMethodUsed: string;
	serviceFee: number;
	totalCharge: number;
}

export interface ISatisfactionSurveyResponse extends ISurveyResponse<Record<string, string | IPublicFileAttachment[]>> {
	externalReviewClickDate?: string; // date
	feedback?: string;
	rating: number;
}

export interface IRecipient {
	id?: string;
	email?: string;
	name?: string;
	firstName?: string;
	lastName?: string;
	phone?: string;
	readonly isPaid?: boolean;
	/**
	 * The ticket type assigned to this attendee/guest
	 */
	ticket?: IEventTicket;
}

export interface IPayment {
	status?: 'NoPayment' | 'PaymentSucceeded' | 'PaymentFailed' | 'PaymentCanceled';
	createdAt?: string; // date
	receiptUrl: string;
	/**
	 * Amount charged in cents
	 */
	amountCharged?: number;
}

export interface IEventSurveyResponse extends ISurveyResponse<Record<string, string | IPublicFileAttachment[]>> {
	anonymousGuestCount?: number;
	attendee?: IRecipient;
	guests?: IRecipient[];
	isAttending?: boolean;
	token?: string;
	payments?: IPayment[];
	/**
	 * The ticket type used for this registration (for paid events with tickets)
	 */
	ticket?: IEventTicket;
}

export enum FormFieldType {
	Boolean = 'Boolean',
	/** Autocomplete field to search for contact */
	Contact = 'Contact',
	Custom = 'Custom',
	/** Date picker */
	Date = 'Date',
	/** Date picker and time picker together */
	DateTime = 'DateTime',
	Decimal = 'Decimal',
	Email = 'Email',
	FileUpload = 'FileUpload',
	/** Requires full address, can be multiple fields but requires street, city, state, and zip */
	FullAddress = 'FullAddress',
	Integer = 'Integer',
	Money = 'Money',
	/** Dropdown | Multi checkboxes that supports multiselect */
	MultipleOptions = 'MultipleOptions',
	/** Note editor */
	Note = 'Note',
	/** Dropdown that only supports a single option to be selected */
	Option = 'Option',
	/** Text input */
	String = 'String',
	/** Textarea */
	Text = 'Text',
	/** Time picker only */
	Time = 'Time',
	/** Autocomplete field to search for user */
	User = 'User',
	/** Custom field for COFFEE that allows demo booking to open in a separate window */
	MeetingScheduler = 'MeetingScheduler',
}

// Note: IFileAttachmentWithURL is defined locally because surveys version
// has additional 'storedFile' property not in SDK
export interface IFileAttachmentWithURL extends IFileAttachment {
	storedFile?: { publicUrl?: string };
	url?: string;
	source?: IAttachmentUploadSource;
}

// IFormField is now imported from SDK

export interface ICustomSurvey extends ISurvey {
	_type: 'CustomSurvey';
	customFormId?: string;
	stats?: ISurveyStats;
	endDate?: string;
	customFormIsDisabled?: boolean;
	options?: {
		contactNameOptions?: IContactFieldOptions;
		contactEmailOptions?: IContactFieldOptions;
		contactPhoneNumberOptions?: IContactFieldOptions;
		contactBirthdayOptions?: IContactFieldOptions;
		contactAddressOptions?: IContactFieldOptions;
		intro?: IRawRichTextContentState;
		companyName?: string;
		sendConfirmationMessage?: boolean;
		includeCompanyLogo?: boolean;
		image?: IFileAttachmentWithURL;
		confirmationMessage?: IRawRichTextContentState;
	};
	customForm?: CustomForm<string>;
	allFieldsRequired?: boolean;
	hasDateRange?: boolean;
	customFieldMap?: ICustomFieldMap[];
}

export interface IPublicFileAttachment {
	fileName: string;
	attachmentId: string;
}

export interface ICustomSurveyResponse extends ISurveyResponse<Record<string, string | IPublicFileAttachment[]>> {
	customProperties?: Record<string, string | IPublicFileAttachment[]>;
	contactInfo?: {
		email?: string;
		firstName?: string;
		lastName?: string;
		phoneNumber?: string;
		birthday?: string;
		address?: IAddress;
	};
	token?: string;
	isDraft?: boolean;
}

// Note: Keeping IDonorInfoCollectionOptions locally as it's a surveys-specific interface
// SDK uses inline types for donation options
export interface IDonorInfoCollectionOptions {
	collect: boolean;
	require: boolean;
	updateExisting: boolean;
}

/**
 * Settings for a specific donation frequency (one-time, monthly, or annual)
 */
export interface IDonationFrequencySettings {
	/** Whether this donation frequency is enabled */
	enabled: boolean;
	/** Whether to show suggested donation amounts */
	enableSuggestedAmounts: boolean;
	/** Array of suggested donation amounts in dollars */
	suggestedAmounts: number[];
	/** Whether donors can enter a custom amount */
	allowCustomAmount: boolean;
	/** Whether a minimum donation amount is required */
	requireMinimumAmount: boolean;
	/** Minimum donation amount in dollars */
	minimumAmount: number;
}

export interface IDonationOptions {
	collectDonorName: IDonorInfoCollectionOptions;
	collectDonorEmail: IDonorInfoCollectionOptions;
	collectDonorPhone: IDonorInfoCollectionOptions;
	collectDonorAddress: IDonorInfoCollectionOptions;
	sendConfirmationEmail: boolean;
	confirmationMessage?: string;
	donationDescription?: string;
	title?: string;
	image?: IFileAttachment;

	// Legacy flat structure (deprecated - use frequency-specific settings instead)
	/** @deprecated Use oneTimeDonationSettings.enableSuggestedAmounts instead */
	enableSuggestedAmounts: boolean;
	/** @deprecated Use oneTimeDonationSettings.suggestedAmounts instead */
	suggestedAmounts: number[];
	/** @deprecated Use oneTimeDonationSettings.allowCustomAmount instead */
	allowCustomAmount: boolean;
	/** @deprecated Use oneTimeDonationSettings.requireMinimumAmount instead */
	requireMinimumAmount: boolean;
	/** @deprecated Use oneTimeDonationSettings.minimumAmount instead */
	minimumAmount: number;

	// New frequency-specific settings structure
	/** Whether recurring donations (monthly/annual) are enabled */
	allowRecurringDonations?: boolean;
	/** Settings for one-time donations */
	oneTimeDonationSettings?: IDonationFrequencySettings;
	/** Settings for monthly recurring donations */
	monthlyDonationSettings?: IDonationFrequencySettings;
	/** Settings for annual recurring donations */
	annualDonationSettings?: IDonationFrequencySettings;
	/** Whether donors must select a campaign before completing the donation */
	requireCampaignSelection?: boolean;
}

export interface IDonationSurveyStats extends ISurveyStats {
	totalDonationAmount?: number;
	averageDonationAmount?: number;
	totalServiceFees?: number;
	donorCoverageRate?: number;
	completionRate?: number;
}

export interface IDonationSurvey extends ISurvey {
	serviceFeeSettings?: IServiceFeeSettings;
	availableCampaignIds?: string[];
	availableCampaigns?: {
		creationDate: Date;
		foregroundColor: string;
		id: string;
		lastModifiedDate: Date;
		name: string;
		_type: 'DonationCampaign';
	}[];
	donationOptions?: IDonationOptions;
	startDate?: string;
	expirationDate?: string;
	stats?: IDonationSurveyStats;
	customForm: CustomForm<string>;
	customFormIsDisabled?: boolean;
}

export interface IDonationSurveyResponse extends ISurveyResponse<Record<string, string | IPublicFileAttachment[]>> {
	donationAmount: number;
	serviceFee: number;
	totalCharge: number;
	donorCoversFees: boolean;
	selectedCampaignId?: string;
	confirmationNumber?: string;
	donor: IDonorInfo;
	payments?: IDonationPayment[];
	donationMirroredDate?: string; // Date
	mirroredDonationId?: string;
	isMirrored: boolean;
	submissionDate?: string; // Date
	customProperties?: Record<string, string | IPublicFileAttachment[]>;
	stripeSubscriptionId?: string;
	stripeCustomerId?: string;
	donationFrequency?: DonationFrequency;
	subscriptionStatus?: SubscriptionStatus;
}
